/**
 * Statistics Page
 * Shows user statistics and analytics with Basic and Advance chart tabs
 *
 * @author Varun Pandey
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  RadarChart,
  Radar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  RadialBarChart,
  RadialBar,
  ScatterChart,
  Scatter,
  ZAxis,
  FunnelChart,
  Funnel,
  LabelList,
} from 'recharts';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { cn } from '../../utils/cn';
import { Loader } from '@/components/ui';

type PlanKey = 'free' | 'economy' | 'premium' | 'elite';

type StatsTab = 'basic' | 'advance';

type PeriodValue = '7d' | '30d' | '90d' | '180d' | '365d';

const PERIOD_OPTIONS: { label: string; value: PeriodValue }[] = [
  { label: '7 days', value: '7d' },
  { label: '1 month', value: '30d' },
  { label: '3 months', value: '90d' },
  { label: '6 months', value: '180d' },
  { label: '1 year', value: '365d' },
];

const TABS: { id: StatsTab; label: string }[] = [
  { id: 'basic', label: 'Basic' },
  { id: 'advance', label: 'Advance' },
];

// Shared chart colors (matches dashboard palette)
const CHART_COLORS = ['#F33363', '#8E8E93', '#3A3A3C', '#636366', '#AEAEB2', '#48484A'];
const GRID_STROKE = '#3A3A3C';
const TEXT_COLOR = '#8E8E93';

interface ProfileViewPoint {
  date: string;
  pageViews: number;
  activeUsers: number;
}

interface BookingTrendPoint {
  date: string;
  bookings: number;
}

interface WeeklyEngagementPoint {
  date: string;
  engagementRate: number;
}

interface TrafficByDevicePoint {
  value: number;
  device: string;
}

type PerformanceRadarRaw = Record<string, number>;

interface EventByTypePoint {
  value: number;
  name?: string;
  type?: string;
}

interface BasicStatsPayload {
  profile_views: ProfileViewPoint[];
  bookings_trend: BookingTrendPoint[];
  weekly_engagement: WeeklyEngagementPoint[];
  traffic_by_device: TrafficByDevicePoint[];
  performance_radar: PerformanceRadarRaw | null;
  event_by_type: EventByTypePoint[];
}

interface BasicStatsResponse {
  success: boolean;
  data: BasicStatsPayload;
}

const formatDateLabel = (isoDate: string) => {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const toTitleCase = (value: string) =>
  value
    .split('_')
    .join(' ')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

// Mock data for Advance charts
const COMPOSED_DATA = [
  { month: 'Jan', revenue: 2400, bookings: 12 },
  { month: 'Feb', revenue: 1800, bookings: 9 },
  { month: 'Mar', revenue: 3200, bookings: 18 },
  { month: 'Apr', revenue: 2800, bookings: 15 },
  { month: 'May', revenue: 3900, bookings: 22 },
  { month: 'Jun', revenue: 4200, bookings: 25 },
];

const STACKED_BAR_DATA = [
  { city: 'Mumbai', direct: 40, referral: 24, organic: 20 },
  { city: 'Delhi', direct: 30, referral: 38, organic: 22 },
  { city: 'Bangalore', direct: 20, referral: 30, organic: 45 },
  { city: 'Chennai', direct: 28, referral: 42, organic: 18 },
];

const STACKED_AREA_DATA = [
  { month: 'Jan', views: 120, clicks: 80 },
  { month: 'Feb', views: 180, clicks: 95 },
  { month: 'Mar', views: 150, clicks: 110 },
  { month: 'Apr', views: 220, clicks: 140 },
  { month: 'May', views: 260, clicks: 165 },
  { month: 'Jun', views: 310, clicks: 190 },
];

const RADIAL_DATA = [
  { name: 'Profile', value: 78, fill: CHART_COLORS[0] },
  { name: 'Response', value: 85, fill: CHART_COLORS[1] },
  { name: 'Rating', value: 92, fill: CHART_COLORS[2] },
  { name: 'Completion', value: 65, fill: CHART_COLORS[3] },
];

const SCATTER_DATA = [
  { x: 100, y: 200, z: 200 },
  { x: 120, y: 100, z: 260 },
  { x: 170, y: 300, z: 400 },
  { x: 140, y: 250, z: 280 },
  { x: 150, y: 400, z: 500 },
  { x: 110, y: 280, z: 200 },
];

const FUNNEL_DATA = [
  { name: 'Profile Views', value: 1000, fill: CHART_COLORS[0] },
  { name: 'Inquiries', value: 400, fill: CHART_COLORS[1] },
  { name: 'Quotes Sent', value: 180, fill: CHART_COLORS[2] },
  { name: 'Bookings', value: 72, fill: CHART_COLORS[3] },
];

/** Card wrapper for each chart - matches JobsPage/MembershipPage card style */
function ChartCard({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] rounded-2xl border border-[#3A3A3C] p-5 lg:p-6',
        className
      )}
    >
      <h3 className="text-sm font-medium text-[#8E8E93] mb-4">{title}</h3>
      <div className="h-[260px] w-full">{children}</div>
    </motion.div>
  );
}

// Advance charts: full Recharts markup kept below for when data is wired; UI shows placeholder only.
const ADVANCE_NO_DATA_TEXT = 'No data available for this graph.';
const showAdvanceNoDataForNow = true;

function AdvanceChartPlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center px-2 text-center text-sm text-[#8E8E93]">
      {ADVANCE_NO_DATA_TEXT}
    </div>
  );
}

/** Recharts tooltip styling to match dark theme */
const tooltipContentStyle = {
  backgroundColor: '#1C1C1E',
  border: '1px solid #3A3A3C',
  borderRadius: '12px',
  padding: '12px 16px',
  fontSize: '12px',
  color: '#fff',
};

export const StatisticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<StatsTab>('basic');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodValue>('7d');
  const [basicStats, setBasicStats] = useState<BasicStatsPayload | null>(null);
  const [isBasicLoading, setIsBasicLoading] = useState(false);
  const [basicError, setBasicError] = useState<string | null>(null);

  const artistSubscriptions = useArtistDetailsStore((s) => s.data?.subscriptions);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

  const subscriptionName = artistSubscriptions?.subscription_name?.toLowerCase() ?? '';
  const hasAdvancedAccess =
    subscriptionName.includes('premium') || subscriptionName.includes('elite');

  useEffect(() => {
    ensureArtistDetailsFetched().catch(() => undefined);
  }, [ensureArtistDetailsFetched]);

  useEffect(() => {
    if (activeTab !== 'basic') return;

    let isCancelled = false;

    const fetchBasicStats = async () => {
      try {
        setIsBasicLoading(true);
        setBasicError(null);

        const response = await apiClient.get<BasicStatsResponse>(
          API_ENDPOINTS.ANALYTICS.STATS_BASIC,
          {
            params: { period: selectedPeriod },
          },
        );

        if (!isCancelled) {
          if (response.data?.success && response.data.data) {
            setBasicStats(response.data.data);
          } else {
            setBasicStats(null);
            setBasicError('Could not load statistics.');
          }
        }
      } catch {
        if (!isCancelled) {
          setBasicStats(null);
          setBasicError('Could not load statistics.');
        }
      } finally {
        if (!isCancelled) {
          setIsBasicLoading(false);
        }
      }
    };

    fetchBasicStats();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, selectedPeriod]);

  const profileViewsData =
    basicStats?.profile_views?.map((item) => ({
      date: formatDateLabel(item.date),
      pageViews: item.pageViews,
      activeUsers: item.activeUsers,
    })) ?? [];

  const bookingsTrendData =
    basicStats?.bookings_trend?.map((item) => ({
      date: formatDateLabel(item.date),
      bookings: (item as any).bookings ?? (item as any).value ?? 0,
    })) ?? [];

  const weeklyEngagementData =
    basicStats?.weekly_engagement?.map((item) => ({
      date: formatDateLabel(item.date),
      engagementRate: item.engagementRate,
    })) ?? [];

  const trafficByDeviceData =
    basicStats?.traffic_by_device?.map((item, index) => ({
      name: toTitleCase(item.device),
      value: item.value,
      color: CHART_COLORS[index % CHART_COLORS.length],
    })) ?? [];

  const performanceRadarData =
    basicStats?.performance_radar && Object.keys(basicStats.performance_radar).length > 0
      ? Object.entries(basicStats.performance_radar).map(([key, value]) => ({
        subject: toTitleCase(key),
        score: value,
        fullMark: 100,
      }))
      : [];

  const eventsByTypeData =
    basicStats?.event_by_type?.map((item, index) => ({
      name: item.name ?? item.type ?? `Type ${index + 1}`,
      value: item.value,
      color: CHART_COLORS[index % CHART_COLORS.length],
    })) ?? [];

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#3A3A3C]">
              <TrendingUp className="h-5 w-5 text-[#8E8E93]" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">Statistics</h1>
          </div>
          <p className="text-[#8E8E93] text-sm lg:text-base">
            View your performance metrics and analytics
          </p>
        </div>

        {/* Tabs + period filter */}
        <div className="mb-6 lg:mb-8 border-b border-[#3A3A3C]">
          <div className="flex items-center justify-between gap-4">
            <nav className="flex gap-6 lg:gap-8" aria-label="Statistics categories">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                    activeTab === tab.id ? 'text:white' : 'text-[#8E8E93] hover:text-white',
                  )}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.span
                      layoutId="stats-tab-underline"
                      className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                      transition={{ type: 'spring', duration: 0.3 }}
                    />
                  )}
                </button>
              ))}
            </nav>

            {activeTab === 'basic' && (
              <div className="flex items-center gap-2 pb-3">
                <span className="hidden text-xs text-[#8E8E93] sm:inline">Time range</span>
                <select
                  className="h-8 rounded-md border border-[#3A3A3C] bg-[#1C1C1E] px-2 text-xs text-white outline-none focus-visible:ring-2 focus-visible:ring-[#F33363]"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as PeriodValue)}
                >
                  {PERIOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Basic tab: 6 chart types */}
        {activeTab === 'basic' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            <ChartCard title="Profile views (Bar)">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  <Loader className="h-4 w-4 text-[#8E8E93]" />
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : profileViewsData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this period.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={profileViewsData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID_STROKE}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      cursor={{ fill: '#2C2C2E' }}
                    />
                    <Bar
                      dataKey="pageViews"
                      fill={CHART_COLORS[0]}
                      radius={[4, 4, 0, 0]}
                      name="Page views"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Bookings trend (Line)">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  <Loader className="h-4 w-4 text-[#8E8E93]" />
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : bookingsTrendData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this graph.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={bookingsTrendData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID_STROKE}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      cursor={{ stroke: GRID_STROKE }}
                    />
                    <Line
                      type="monotone"
                      dataKey="bookings"
                      stroke={CHART_COLORS[0]}
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS[0], r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Weekly engagement (Area)">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  <Loader className="h-4 w-4 text-[#8E8E93]" />
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : weeklyEngagementData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this graph.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={weeklyEngagementData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <defs>
                      <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor={CHART_COLORS[0]}
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="100%"
                          stopColor={CHART_COLORS[0]}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID_STROKE}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis
                      stroke={TEXT_COLOR}
                      fontSize={11}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      cursor={{ stroke: GRID_STROKE }}
                    />
                    <Area
                      type="monotone"
                      dataKey="engagementRate"
                      stroke={CHART_COLORS[0]}
                      fill="url(#areaGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Traffic by device (Pie)">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  <Loader className="h-4 w-4 text-[#8E8E93]" />
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : trafficByDeviceData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this graph.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={trafficByDeviceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius="80%"
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                      }
                    >
                      {trafficByDeviceData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          stroke="#1C1C1E"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      formatter={(value) => [`${value ?? 0}%`, 'Share']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Performance radar">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  Loading statistics...
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : performanceRadarData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this graph.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    cx="50%"
                    cy="50%"
                    outerRadius="70%"
                    data={performanceRadarData}
                  >
                    <PolarGrid stroke={GRID_STROKE} />
                    <PolarAngleAxis
                      dataKey="subject"
                      stroke={TEXT_COLOR}
                      fontSize={10}
                    />
                    <PolarRadiusAxis
                      stroke={TEXT_COLOR}
                      fontSize={10}
                      tick={false}
                    />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke={CHART_COLORS[0]}
                      fill={CHART_COLORS[0]}
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                    <Tooltip contentStyle={tooltipContentStyle} />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Events by type (Donut)">
              {isBasicLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  <Loader className="h-4 w-4 text-[#8E8E93]" />
                </div>
              ) : basicError ? (
                <div className="flex h-full items-center justify-center text-sm text-[#F33363]">
                  {basicError}
                </div>
              ) : eventsByTypeData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-[#8E8E93]">
                  No data available for this graph.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={eventsByTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius="55%"
                      outerRadius="80%"
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {eventsByTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          stroke="#1C1C1E"
                          strokeWidth={2}
                        />
                      ))}
                      <LabelList
                        dataKey="name"
                        position="outside"
                        stroke={TEXT_COLOR}
                        fontSize={10}
                      />
                    </Pie>
                    <Tooltip
                      contentStyle={tooltipContentStyle}
                      formatter={(value) => [`${value ?? 0}%`, 'Share']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        )}

        {/* Advance tab: 6 chart types (Premium & Elite only) */}
        {activeTab === 'advance' && (
          <>
            {!hasAdvancedAccess && (
              <div className="mb-4 rounded-2xl border border-dashed border-[#3A3A3C] bg-[#121214] px-4 py-3 text-sm text-[#E5E5EA]">
                Advanced statistics are available only on <span className="font-semibold">Premium</span> and{' '}
                <span className="font-semibold">Elite</span> membership plans. Upgrade your membership to unlock detailed performance insights.
              </div>
            )}

            <div
              className={cn(
                'grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6',
                !hasAdvancedAccess && 'pointer-events-none blur-sm opacity-60',
              )}
            >
              <ChartCard title="Revenue & bookings (Composed)">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={COMPOSED_DATA}
                      margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={GRID_STROKE}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="month"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="left"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        cursor={{ fill: '#2C2C2E' }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="revenue"
                        fill={CHART_COLORS[1]}
                        radius={[4, 4, 0, 0]}
                        name="Revenue (₹)"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="bookings"
                        stroke={CHART_COLORS[0]}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS[0], r: 3 }}
                        name="Bookings"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Traffic source by city (Stacked Bar)">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={STACKED_BAR_DATA}
                      margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                      layout="vertical"
                      barCategoryGap="20%"
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={GRID_STROKE}
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="city"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                        width={60}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        cursor={{ fill: '#2C2C2E' }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Bar
                        dataKey="direct"
                        stackId="a"
                        fill={CHART_COLORS[0]}
                        radius={[0, 0, 0, 0]}
                        name="Direct"
                      />
                      <Bar
                        dataKey="referral"
                        stackId="a"
                        fill={CHART_COLORS[1]}
                        radius={[0, 0, 0, 0]}
                        name="Referral"
                      />
                      <Bar
                        dataKey="organic"
                        stackId="a"
                        fill={CHART_COLORS[2]}
                        radius={[0, 4, 4, 0]}
                        name="Organic"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Views & clicks (Stacked Area)">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={STACKED_AREA_DATA}
                      margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                    >
                      <defs>
                        <linearGradient id="stackViews" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor={CHART_COLORS[0]}
                            stopOpacity={0.4}
                          />
                          <stop
                            offset="100%"
                            stopColor={CHART_COLORS[0]}
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient id="stackClicks" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="0%"
                            stopColor={CHART_COLORS[1]}
                            stopOpacity={0.4}
                          />
                          <stop
                            offset="100%"
                            stopColor={CHART_COLORS[1]}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={GRID_STROKE}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="month"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        cursor={{ stroke: GRID_STROKE }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Area
                        type="monotone"
                        dataKey="views"
                        stackId="1"
                        stroke={CHART_COLORS[0]}
                        fill="url(#stackViews)"
                        strokeWidth={2}
                        name="Views"
                      />
                      <Area
                        type="monotone"
                        dataKey="clicks"
                        stackId="1"
                        stroke={CHART_COLORS[1]}
                        fill="url(#stackClicks)"
                        strokeWidth={2}
                        name="Clicks"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Metrics (Radial Bar)">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      cx="50%"
                      cy="50%"
                      innerRadius="25%"
                      outerRadius="85%"
                      barSize={12}
                      data={RADIAL_DATA}
                    >
                      <RadialBar
                        background
                        dataKey="value"
                        cornerRadius={6}
                        label={{
                          position: 'insideStart',
                          fill: '#fff',
                          fontSize: 10,
                        }}
                      >
                        {RADIAL_DATA.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </RadialBar>
                      <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        formatter={(value) => [`${value ?? 0}%`, 'Score']}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard title="Engagement scatter">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                      <XAxis
                        type="number"
                        dataKey="x"
                        name="Views"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                      />
                      <YAxis
                        type="number"
                        dataKey="y"
                        name="Clicks"
                        stroke={TEXT_COLOR}
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ZAxis type="number" dataKey="z" range={[100, 600]} name="Value" />
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        cursor={{ strokeDasharray: '3 3' }}
                      />
                      <Scatter
                        name="Sessions"
                        data={SCATTER_DATA}
                        fill={CHART_COLORS[0]}
                        fillOpacity={0.7}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>


              <ChartCard title="Conversion funnel">
                {showAdvanceNoDataForNow ? (
                  <AdvanceChartPlaceholder />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <FunnelChart>
                      <Tooltip contentStyle={tooltipContentStyle} />
                      <Funnel dataKey="value" data={FUNNEL_DATA} isAnimationActive>
                        <LabelList
                          position="right"
                          fill="#8E8E93"
                          stroke="none"
                          dataKey="name"
                          fontSize={11}
                        />
                        <LabelList
                          position="center"
                          fill="#fff"
                          stroke="none"
                          dataKey="value"
                          fontSize={11}
                        />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
